// src/components/forms/register/RegisterAlumniForm.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AlertCircle, User, BookOpen, Users, Check } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import BasicInfoSection from "../forms/register/BasicInfoSection";
import ProfileSection from "../forms/register/ProfileSection";
import NextOfKinSection from "../forms/register/NextOfKinSection";

const steps = [
  { id: 1, title: "Account", icon: User, description: "Basic account information" },
  { id: 2, title: "Profile", icon: BookOpen, description: "Personal details" },
  { id: 3, title: "Next of Kin", icon: Users, description: "Emergency contact" },
];

const RegisterAlumniForm = ({ inModal = false, onSuccess, onCancel }) => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    role: "alumni",
    phone: "",
    fullName: "",
    studentInfo: {
      studentId: "",
      enrollmentYear: "",
      // intakeSemester and yearOfStudy are omitted for alumni
      major: "",
      profile: {
        gender: "",
        dateOfBirth: "",
        nationality: "",
        religion: "",
        residence: "",
        maritalStatus: "",
      },
      nextOfKin: {
        name: "",
        relation: "",
        email: "",
        phone: "",
      },
    },
  });

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return (
          formData.username.trim() &&
          formData.email.trim() &&
          formData.password.length >= 6 &&
          formData.studentInfo.studentId.trim()
        );
      case 2:
        return (
          formData.fullName.trim() &&
          formData.studentInfo.profile.gender.trim() &&
          formData.studentInfo.profile.dateOfBirth.trim() &&
          formData.studentInfo.profile.nationality.trim() &&
          formData.studentInfo.profile.residence.trim() &&
          formData.studentInfo.profile.maritalStatus.trim() &&
          formData.phone.trim() &&
          formData.studentInfo.enrollmentYear.trim() &&
          formData.studentInfo.major.trim()
        );
      case 3:
        return (
          formData.studentInfo.nextOfKin.name.trim() &&
          formData.studentInfo.nextOfKin.relation.trim() &&
          formData.studentInfo.nextOfKin.phone.trim()
        );
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length));
      setError("");
    } else {
      setError("Please fill in all required fields");
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(currentStep)) {
      setError("Please fill in all required fields");
      return;
    }

    setError("");
    setLoading(true);

    try {
      await register(formData);
      navigate("/dashboard");
    } catch (err) {
      setError("Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${inModal ? '' : 'min-h-screen'} flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 py-12 px-4 sm:px-6 lg:px-8`}>
      <div className="max-w-4xl w-full space-y-8">
        {!inModal && (
          <div className="text-center">
            <Link to="/" className="inline-flex items-center gap-3 mb-6">
              <img
                src="/usiu-logo-02.png"
                alt="USIU Logo"
                className="h-12 w-12 object-contain"
                onError={(e) => {
                  e.target.src = "/usiu-logo.png";
                }}
              />
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-usiu-blue-600 to-usiu-blue-500 dark:from-usiu-gold-400 dark:to-usiu-gold-500 bg-clip-text text-transparent">
                  USIU-Africa
                </h1>
                <p className="text-xs text-gray-600 dark:text-gray-300">Counseling Center</p>
              </div>
            </Link>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-800 dark:from-usiu-gold-400 dark:to-usiu-gold-500 bg-clip-text text-transparent">
              Alumni Registration
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Create your account to access counseling services
            </p>
          </div>
        )}

        {/* Progress Steps */}
        <nav aria-label="Progress">
          <ol className="flex items-center justify-center">
            {steps.map((step, index) => (
              <li
                key={step.id}
                className={`relative ${index !== steps.length - 1 ? "pr-20" : ""
                  }`}
              >
                {index !== steps.length - 1 && (
                  <div
                    className="absolute top-1/2 left-full -translate-y-1/2 transform"
                    style={{ left: "calc(50% + 1.5rem)", width: "10rem" }}
                  >
                    <div
                      className={`h-0.5 w-full ${step.id < currentStep
                        ? "bg-gradient-to-r from-green-500 to-green-600"
                        : "bg-gray-200 dark:bg-gray-700"
                        }`}
                    />
                  </div>
                )}

                <div className="group relative flex flex-col items-center">
                  <span className="flex items-center">
                    <span
                      className={`flex h-12 w-12 items-center justify-center rounded-full ${step.id < currentStep
                        ? "bg-gradient-to-r from-green-500 to-green-600"
                        : step.id === currentStep
                          ? "bg-gradient-to-r from-usiu-blue-600 to-usiu-blue-500"
                          : "bg-gray-200 dark:bg-gray-700"
                        } transition-colors duration-150 ease-in-out`}
                    >
                      {step.id < currentStep ? (
                        <Check className="h-6 w-6 text-white" />
                      ) : (
                        <step.icon
                          className={`h-6 w-6 ${step.id === currentStep
                            ? "text-white"
                            : "text-gray-500 dark:text-gray-400"
                            }`}
                        />
                      )}
                    </span>
                  </span>
                  <div className="mt-3 text-center">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {step.title}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </nav>

        {error && (
          <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-300">{error}</h3>
              </div>
            </div>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-xl shadow-sm space-y-6">
            {currentStep === 1 && (
              <BasicInfoSection
                formData={formData}
                setFormData={setFormData}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
              />
            )}

            {currentStep === 2 && (
              <ProfileSection formData={formData} setFormData={setFormData} />
            )}

            {currentStep === 3 && (
              <NextOfKinSection formData={formData} setFormData={setFormData} />
            )}
          </div>

          <div className="flex justify-between space-x-4">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={inModal ? onCancel : prevStep}
                className="flex-1 py-2 px-4 border border-gray-300 dark:border-gray-700 rounded-xl shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-usiu-blue-400"
              >
                {inModal ? 'Cancel' : 'Previous'}
              </button>
            )}

            {currentStep < steps.length ? (
              <button
                type="button"
                onClick={nextStep}
                className="flex-1 py-2 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-usiu-blue-500 hover:bg-usiu-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-usiu-blue-400"
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-2 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-usiu-blue-500 hover:bg-usiu-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-usiu-blue-400 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Creating account...
                  </div>
                ) : (
                  "Create account"
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterAlumniForm;
